import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { Crosshair, ExternalLink } from 'lucide-react';
import luckblocksLogo from '../../assets/images/luckblocks.png';
import { TIERS, explorerAddress } from './config';
import { useT } from '../../i18n';

/** Tiers read from the contract, provided by the BullsEye page (defaults until the first read). */
export const TiersContext = createContext(TIERS);

/* -------------------------------------------------------------------------- */
/* Formatting                                                                  */
/* -------------------------------------------------------------------------- */

export const shortAddr = (value) =>
  value ? `${value.slice(0, 6)}...${value.slice(-4)}` : '';

export const usd = (value, digits = 0) =>
  Number.isFinite(value)
    ? value.toLocaleString('en-US', {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
      })
    : '-';

/**
 * The contract counts months as fixed 30-day buckets since the epoch, so the
 * raw index means nothing to a player, so turn it into the dates it covers.
 */
export const monthRange = (month) => {
  if (!month) return '-';
  const period = 30 * 86400 * 1000;
  const start = new Date(month * period);
  const end = new Date((month + 1) * period);
  const format = (date) =>
    date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  return `${format(start)} to ${format(end)}`;
};

/** WBTC amounts are tiny; trailing zeros make them unreadable. */
export const btc = (value) => {
  if (!Number.isFinite(value)) return '-';
  if (value === 0) return '0';
  if (value < 0.00000001) return '< 0.00000001';
  return value.toFixed(8).replace(/0+$/, '').replace(/\.$/, '');
};

/* -------------------------------------------------------------------------- */
/* Pieces                                                                      */
/* -------------------------------------------------------------------------- */

export const AddressLink = ({ value, className = '' }) => (
  <a
    href={explorerAddress(value)}
    target="_blank"
    rel="noopener noreferrer"
    className={`lb-link notranslate lb-num inline-flex items-center gap-1 ${className}`}
  >
    {shortAddr(value)}
    <ExternalLink className="h-3 w-3 opacity-60" aria-hidden="true" />
  </a>
);

export const StatTile = ({ label, value, sub, tone = 'default', className = '' }) => {
  const valueTone =
    tone === 'gold' ? 'lb-gold-text' : tone === 'gradient' ? 'lb-gradient-text' : 'text-white';

  return (
    <div className={`lb-tile p-4 ${className}`}>
      <p className="lb-label">{label}</p>
      <p className={`notranslate lb-num mt-2 font-display text-xl leading-none ${valueTone}`}>
        {value}
      </p>
      {sub ? <p className="notranslate mt-1.5 text-xs text-white/45">{sub}</p> : null}
    </div>
  );
};

/** Colour-codes a prize tier: exact hits are gold, the margins cool down. */
export const TierBadge = ({ tier, className = '' }) => {
  const t = useT();
  const tiers = useContext(TiersContext);
  const meta = tiers[tier] || tiers[tiers.length - 1];
  const tone = meta.jackpot
    ? 'border-amber-400/40 bg-amber-400/10 text-amber-300'
    : tier <= 2
    ? 'border-cyan-400/30 bg-cyan-400/10 text-cyan-200'
    : 'border-white/15 bg-white/5 text-white/60';

  return (
    <span
      className={`inline-flex items-center rounded-full border px-2.5 py-1 text-[0.65rem] font-bold uppercase tracking-[0.16em] ${tone} ${className}`}
    >
      {meta.labelKey ? t(meta.labelKey) : meta.label}
    </span>
  );
};

/**
 * Countdown to a round deadline.
 *
 * Rounds on this deployment can sit past their end time while they wait for the
 * keeper to resolve them, so an expired timer reports that state rather than
 * counting backwards into negative numbers.
 */
// chain clock minus local clock, set by useBullsEye from the latest block, so countdowns follow the contract
let chainOffsetMs = 0;
export const setChainOffset = (ms) => {
  chainOffsetMs = ms;
};
export const chainNow = () => Date.now() + chainOffsetMs;

export const Countdown = ({ endTime, className = '' }) => {
  const t = useT();
  const [now, setNow] = useState(() => chainNow());

  useEffect(() => {
    const id = setInterval(() => setNow(chainNow()), 1000);
    return () => clearInterval(id);
  }, []);

  const remaining = endTime ? endTime - now : 0;

  if (!endTime) {
    return <span className={`notranslate lb-num ${className}`}>-</span>;
  }

  if (remaining <= 0) {
    return (
      <span className={`text-amber-300 ${className}`}>{t('bullseye.awaitingResolution')}</span>
    );
  }

  const total = Math.floor(remaining / 1000);
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  const pad = (unit) => String(unit).padStart(2, '0');

  return (
    <span className={`notranslate lb-num ${className}`}>
      {pad(hours)}:{pad(minutes)}:{pad(seconds)}
    </span>
  );
};

/**
 * A single prediction, drawn as a ticket stub so it reads the same as the
 * raffle tickets elsewhere in the app.
 */
export const PredictionTicket = ({
  price,
  roundId,
  index,
  status = 'pending',
  tier = null,
  prize = null,
  onRemove = null,
}) => {
  const t = useT();
  const barcode = useMemo(
    () =>
      Array.from({ length: 26 }, (slot, i) => ((price + i * 7) % 5) + 1),
    [price]
  );

  const tone =
    status === 'won'
      ? 'border-emerald-400/40'
      : status === 'lost'
      ? 'border-white/10'
      : 'border-cyan-400/25';

  return (
    <article
      className={`lb-hoverable relative flex w-full flex-col overflow-hidden rounded-[22px] border ${tone} bg-gradient-to-b from-white/[0.06] via-black/60 to-black p-4 text-white`}
    >
      <header className="flex items-start justify-between gap-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-white/15 bg-white/5">
          <Crosshair className="h-4 w-4 text-brand-cyan" aria-hidden="true" />
        </span>
        <div className="text-right">
          <p className="lb-label">{t('bullseye.prediction')}</p>
          <p className="notranslate lb-num text-xs text-white/45">
            {roundId ? t('bullseye.liveRoundLabel', { round: roundId }) : `#${index + 1}`}
          </p>
        </div>
      </header>

      <p className="notranslate lb-num mt-4 text-center font-display text-2xl leading-none lb-gradient-text">
        ${usd(price)}
      </p>

      {tier !== null ? (
        <div className="mt-3 flex justify-center">
          <TierBadge tier={tier} />
        </div>
      ) : null}

      {prize ? (
        <p className="notranslate lb-num mt-3 text-center text-sm font-bold text-emerald-300">
          +{prize}
        </p>
      ) : null}

      <div className="relative my-3 flex items-center" aria-hidden="true">
        <span className="h-3 w-3 -translate-x-[18px] rounded-full bg-black" />
        <span className="flex-1 border-t border-dashed border-white/20" />
        <span className="h-3 w-3 translate-x-[18px] rounded-full bg-black" />
      </div>

      <div className="flex items-end justify-center gap-[2px]" aria-hidden="true">
        {barcode.map((width, i) => (
          <span
            key={i}
            className="h-5 rounded-sm bg-white/25"
            style={{ width: `${width}px` }}
          />
        ))}
      </div>

      <img
        src={luckblocksLogo}
        alt=""
        aria-hidden="true"
        className="mx-auto mt-3 w-24 opacity-40"
      />

      {onRemove ? (
        <button
          type="button"
          onClick={onRemove}
          className="lb-btn lb-btn-ghost lb-btn-sm mt-3 w-full"
        >
          {t('bullseye.remove')}
        </button>
      ) : null}
    </article>
  );
};

export default PredictionTicket;
